import { createApi } from '@reduxjs/toolkit/query/react';
import {
  WorkshopTask,
  CreateWorkshopTaskBody,
  UpdateWorkshopTaskBody,
  FinishWorkshopTaskBody,
  GetWorkshopTasksParams,
  GetWorkshopTasksResponse,
  WorkshopTaskStats,
  GetWorkshopTaskStatsResponse
} from '../../resources/types/workshopTasksTypes';
import baseQuery from './baseQuery';

export const workshopTasksApi = createApi({
  reducerPath: 'workshopTasksApi',
  baseQuery,
  tagTypes: ['WorkshopTask', 'WorkshopTaskStats'],
  endpoints: builder => ({
    getWorkshopTasks: builder.query<GetWorkshopTasksResponse, GetWorkshopTasksParams>({
      query: params => ({
        url: 'workshop/tasks',
        params
      }),
      providesTags: ['WorkshopTask']
    }),
    getWorkshopTaskStats: builder.query<WorkshopTaskStats, void>({
      query: () => 'workshop/stats',
      transformResponse(response: GetWorkshopTaskStatsResponse) {
        return response.data;
      },
      providesTags: ['WorkshopTaskStats']
    }),
    createWorkshopTask: builder.mutation<WorkshopTask, CreateWorkshopTaskBody>({
      query: body => ({
        url: 'workshop/tasks',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: WorkshopTask }) {
        return response.data;
      },
      invalidatesTags: ['WorkshopTask', 'WorkshopTaskStats']
    }),
    updateWorkshopTask: builder.mutation<WorkshopTask, { id: string; body: UpdateWorkshopTaskBody }>({
      query: ({ id, body }) => ({
        url: `workshop/tasks/${id}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: WorkshopTask }) {
        return response.data;
      },
      invalidatesTags: ['WorkshopTask', 'WorkshopTaskStats']
    }),
    startWorkshopTask: builder.mutation<WorkshopTask, string>({
      query: id => ({
        url: `workshop/tasks/${id}/start`,
        method: 'PUT',
        body: {}
      }),
      transformResponse(response: { data: WorkshopTask }) {
        return response.data;
      },
      invalidatesTags: ['WorkshopTask', 'WorkshopTaskStats']
    }),
    finishWorkshopTask: builder.mutation<WorkshopTask, { id: string; body: FinishWorkshopTaskBody }>({
      query: ({ id, body }) => ({
        url: `workshop/tasks/${id}/finish`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: WorkshopTask }) {
        return response.data;
      },
      invalidatesTags: ['WorkshopTask', 'WorkshopTaskStats']
    }),
    deleteWorkshopTask: builder.mutation<void, string>({
      query: id => ({
        url: `workshop/tasks/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['WorkshopTask', 'WorkshopTaskStats']
    })
  })
});

export const {
  useGetWorkshopTasksQuery,
  useLazyGetWorkshopTasksQuery,
  useGetWorkshopTaskStatsQuery,
  useCreateWorkshopTaskMutation,
  useUpdateWorkshopTaskMutation,
  useStartWorkshopTaskMutation,
  useFinishWorkshopTaskMutation,
  useDeleteWorkshopTaskMutation
} = workshopTasksApi;
